import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    FormControl,
    FormControlLabel,
    IconButton,
    Radio,
    RadioGroup,
    Skeleton,
    TableCell,
    TableRow,
    Theme,
    Typography,
    useTheme
} from "@mui/material";
import { MandateListItem, MandatesListResBody } from "../../../redux-store/types/investorProfile";
import {
    FormikValues,
    useField,
    useFormikContext
} from "formik";
import { CheckedRadio, DialogCloseIcon, UncheckRadio } from "../../../assets/SvgIcons";
import React, { useEffect, useState } from "react";
import CustomTable from "../common/customTable";
import { TableHeaderData } from "../types";
import { toINR } from "../../../utils/utilityFunctions";
import dayjs from "dayjs";
import { useDispatch, useSelector } from "react-redux";
import { RootStateType } from "../../../redux-store/reducers";
import MandateCreationForm from "./MandateCreationForm";
import { getMandatesList } from "../../../redux-store/actions/investorProfile";

export const MandateInfoBox = ({
    name,
    isLoading,
    isDisabled,
    activeMandateList,
    id,
    activeMandateCount,
    isComingFromIndFund = false
}: {
    name: string;
    isLoading: boolean;
    isDisabled: boolean;
    activeMandateList: Array<MandateListItem>;
    id: string | number;
    activeMandateCount?: boolean;
    isComingFromIndFund?: boolean
}): JSX.Element => {
    const { values } = useFormikContext() as { values: FormikValues }

    const [_, meta] = useField(name)
    const errorText = (meta.error && meta.touched) ? meta.error : '';

    const mandateStatus = values?.fund?.[id]?.sip?.curMandateStatus ?? "";

    let selectedMandateObj = (activeMandateList ?? [])?.find(obj => `${obj?.mandateId ?? ""}` === `${values?.fund?.[id]?.sip?.active?.mandateId ?? ""}`)

    const bankName = values?.fund?.[id]?.sip?.pending?.bankName || values?.fund?.[id]?.sip?.mandate?.bankName;
    const accountNo = values?.fund?.[id]?.sip?.pending?.accountNo || values?.fund?.[id]?.sip?.active?.accountNo || values?.fund?.[id]?.sip?.mandate?.accountNo;

    if (values?.fund?.[id]?.sip?.curMandateStatus === "") {
        // selectedMandateObj = (activeMandateList ?? [])?.find(obj => `${obj?.mandateId ?? ""}` === `${values?.fund?.[id]?.sip?.mandate?.mandateId ?? ""}`)
        selectedMandateObj = activeMandateList?.length > 0 ? activeMandateList?.[0] : undefined
    }

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: '10px'
            }}
        >
            {isLoading && (
                <>
                    <Skeleton animation="wave" width={100} />
                    <Skeleton
                        variant="rectangular"
                        height={50}
                        sx={{ borderRadius: '10px' }}
                    />
                </>
            )}
            {!isLoading && (
                <>
                    <Typography
                        variant="body_500"
                        sx={(theme: Theme) => ({
                            textWrap: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            color: theme.palette.text.secondary
                        })}
                    >
                        {mandateStatus === 'active' ? "Mandate" : 'New Mandate Created'}
                    </Typography>
                    <Box
                        sx={(theme: Theme) => ({
                            display: "flex",
                            alignItems: "center",
                            background: errorText ? theme.palette.error[50] : theme.palette.primary[200],
                            padding: '15px',
                            borderRadius: '10px',
                            opacity: isDisabled ? 0.5 : 1,
                            height: '50px',
                        })}
                    >
                        <Typography
                            variant="body_500"
                            sx={(theme: Theme) => ({
                                color: theme.palette.primary[500],
                                opacity: isDisabled ? 0.9 : 1,
                                fontSize: { lg: '16px', md: '16px', sm: '14px', xs: '14px' }
                            })}
                        >
                            {mandateStatus === 'pending' ? `${bankName} - ${accountNo}` : (selectedMandateObj?.displayText ?? "")}
                        </Typography>
                    </Box>
                </>
            )}
            {errorText &&
                (isLoading ? (
                    <Skeleton variant="text" animation="wave" width={200} />
                ) : <Typography variant="body_400" sx={(theme: Theme) => ({
                    color: theme.palette.error[500]
                })}>
                    {errorText || ''}
                </Typography>)
            }
        </Box >
    )
}

const tableHeaderData: TableHeaderData[] = [
    {
        id: "checkbox",
        label: "",
        align: "left",
        isSortable: false
    },
    {
        id: "bankName",
        label: "BANK NAME",
        align: "center",
        isSortable: false
    },
    {
        id: "accountNumber",
        label: "ACCOUNT NUMBER",
        align: "center",
        isSortable: false
    },
    {
        id: "amount",
        label: "AMOUNT",
        align: "center",
        isSortable: false
    },
    {
        id: "mandateId",
        label: "MANDATE ID",
        align: "center",
        isSortable: false
    },
    {
        id: "startDate",
        label: "START DATE",
        align: "center",
        isSortable: false
    },
    {
        id: "endDate",
        label: "END DATE",
        align: "center",
        isSortable: false
    },
    {
        id: "status",
        label: "STATUS",
        align: "center",
        isSortable: false
    },
]

export const MandateTableBody = ({
    data,
    selectedMandateId,
    handleRadioBtnClick
}: {
    data: Array<MandateListItem>;
    selectedMandateId: string;
    handleRadioBtnClick: (mandateId: string, mandateStatus: string, mandateCreationDate: string, activeObj?: any) => void
}): JSX.Element => {

    const { locale, currencyCode } = useSelector((store: RootStateType) => store.currencyInfo);

    return (
        <>
            {data.map((obj: MandateListItem) => (
                <TableRow
                    key={obj.mandateId}
                    sx={(theme: Theme) => ({
                        '& td': {
                            paddingTop: "20px",
                            paddingBottom: "15px",
                            borderBottom: `0.5px solid ${theme.palette.text.secondary}B2`,
                            ':first-of-type': {
                                paddingLeft: "0px",
                            }
                        },
                    })}
                >
                    <TableCell align="left">
                        <Radio
                            checked={obj?.mandateId === selectedMandateId}
                            onChange={() => handleRadioBtnClick(obj?.mandateId ?? "", obj?.status ?? "", obj?.startDate ?? "", obj ?? {})}
                            value=""
                            name="mandate-select"
                        />
                    </TableCell>
                    <TableCell align="center">
                        <Typography
                            variant="body_500"
                            sx={(theme: Theme) => ({
                                color: theme.palette.text.primary
                            })}
                        >
                            {obj?.bankName ?? ""}
                        </Typography>
                    </TableCell>
                    <TableCell align="center">
                        <Typography
                            variant="body_500"
                            sx={(theme: Theme) => ({
                                color: theme.palette.text.primary
                            })}
                        >
                            {obj?.accountNo ?? ""}
                        </Typography>
                    </TableCell>
                    <TableCell align="center">
                        <Typography
                            variant="body_500"
                            sx={(theme: Theme) => ({
                                color: theme.palette.text.primary
                            })}
                        >
                            {toINR({
                                value: obj?.amount ?? "",
                                locale,
                                currencyCode
                            })}
                        </Typography>
                    </TableCell>
                    <TableCell align="center">
                        <Typography
                            variant="body_500"
                            sx={(theme: Theme) => ({
                                color: theme.palette.text.primary
                            })}
                        >
                            {obj?.mandateId ?? ""}
                        </Typography>
                    </TableCell>
                    <TableCell align="center">
                        <Typography
                            variant="body_500"
                            sx={(theme: Theme) => ({
                                color: theme.palette.text.primary
                            })}
                        >
                            {obj?.startDate ? dayjs(obj.startDate).format("DD/MM/YYYY") : "-"}
                        </Typography>
                    </TableCell>
                    <TableCell align="center">
                        <Typography
                            variant="body_500"
                            sx={(theme: Theme) => ({
                                color: theme.palette.text.primary
                            })}
                        >
                            {obj?.endDate ? dayjs(obj.endDate).format("DD/MM/YYYY") : "-"}
                        </Typography>
                    </TableCell>
                    <TableCell align="center">
                        <Typography
                            variant="body_500"
                            sx={(theme: Theme) => ({
                                color: theme.palette.text.primary
                            })}
                        >
                            {obj?.status ? obj?.status : "-"}
                        </Typography>
                    </TableCell>
                </TableRow>
            ))
            }
        </>
    )
}




function MandateMobileBody({
    data,
    selectedMandateId,
    handleRadioBtnClick
}: {
    data: Array<MandateListItem>;
    selectedMandateId: string;
    handleRadioBtnClick: (mandateId: string, mandateStatus: string, mandateCreationDate: string, activeObj?: any) => void
}): JSX.Element {

    const { locale, currencyCode } = useSelector((store: RootStateType) => store.currencyInfo);
    const getHeaderLabel = (id: string): string => (tableHeaderData.find(h => h.id === id)?.label ?? id);

    return (
        <>
            {data.map((obj: MandateListItem) => (
                <Box
                    key={obj.mandateId}
                    sx={(theme: Theme) => ({
                        border: `1px solid ${theme.palette.primary[200] ?? theme.palette.primary[100]}`,
                        borderRadius: '10px',
                        padding: '8px',
                        mb: '10px',
                        backgroundColor: theme.palette.common.white
                    })}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: '8px' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <Radio
                                checked={obj?.mandateId === selectedMandateId}
                                onChange={() => handleRadioBtnClick(obj?.mandateId ?? "", obj?.status ?? "", obj?.startDate ?? "", obj ?? {})}
                                value=""
                                name="mandate-select"
                            />
                            <Typography variant="body_600" sx={(theme: Theme) => ({ color: theme.palette.text.primary })}>
                                {obj?.bankName ?? ""}
                            </Typography>
                        </Box>
                        <Typography variant="body_500" sx={(theme: Theme) => ({ color: theme.palette.text.secondary })}>
                            {obj?.mandateId ?? ""}
                        </Typography>
                    </Box>

                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                            <Typography variant="body_400" sx={(theme: Theme) => ({ color: theme.palette.text.secondary })}>{getHeaderLabel('accountNumber')}</Typography>
                            <Typography variant="body_500" sx={(theme: Theme) => ({ color: theme.palette.text.primary })}>{obj?.accountNo ?? ""}</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                            <Typography variant="body_400" sx={(theme: Theme) => ({ color: theme.palette.text.secondary })}>{getHeaderLabel('amount')}</Typography>
                            <Typography variant="body_500" sx={(theme: Theme) => ({ color: theme.palette.text.primary })}>
                                {toINR({ value: obj?.amount ?? "", locale, currencyCode })}
                            </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                            <Typography variant="body_400" sx={(theme: Theme) => ({ color: theme.palette.text.secondary })}>{getHeaderLabel('startDate')}</Typography>
                            <Typography variant="body_500" sx={(theme: Theme) => ({ color: theme.palette.text.primary })}>
                                {obj?.startDate ? dayjs(obj.startDate).format("DD/MM/YYYY") : "-"}
                            </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                            <Typography variant="body_400" sx={(theme: Theme) => ({ color: theme.palette.text.secondary })}>{getHeaderLabel('endDate')}</Typography>
                            <Typography variant="body_500" sx={(theme: Theme) => ({ color: theme.palette.text.primary })}>
                                {obj?.endDate ? dayjs(obj.endDate).format("DD/MM/YYYY") : "-"}
                            </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                            <Typography variant="body_400" sx={(theme: Theme) => ({ color: theme.palette.text.secondary })}>{getHeaderLabel('status')}</Typography>
                            <Typography variant="body_500" sx={(theme: Theme) => ({ color: theme.palette.text.primary })}>
                                {obj?.status ? obj?.status : "-"}
                            </Typography>
                        </Box>
                    </Box>
                </Box>
            ))}
        </>
    )
}



export const MandateSelectionDialog = ({
    open,
    onClose,
    currentMandateId,
    mandateList,
    handleMandateSelection,
    mandateDialogOptions,
    curMandateOption,
    setCurMandateOption,
    clientCode,
    handleCreateMandateStatus,
    prefilledValues,
    activeObject
}: {
    open: boolean;
    onClose: () => void;
    currentMandateId: string;
    mandateList: Array<MandateListItem>;
    handleMandateSelection: (mandateId: string, mandateStatus: string, mandCreationDate: string, activeMandateObj?: any) => void;
    mandateDialogOptions?: Array<{ id: number, label: string, value: string }>;
    curMandateOption?: number;
    setCurMandateOption: React.Dispatch<React.SetStateAction<number>>;
    handleCreateMandateStatus?: (mandateObj: any, freshMandatePayload?: any) => void;
    clientCode?: string;
    prefilledValues: any;
    activeObject?: any;
}): JSX.Element => {
    const dispatch = useDispatch();
    const [selectedMandateId, setSelectedMandateId] = useState<string>(currentMandateId);
    const [selectedMandateStatus, setSelectedMandateStatus] = useState<string>("");
    const [mandateCreationDate, setMandateCreationDate] = useState<string>("");
    const activeObjSize = Object.keys(activeObject ?? {}).length;
    const [activeMandateObj, setActiveMandateObj] = useState<any>(activeObjSize > 1 ? activeObject : {});
    const [freshMandatesList, setFreshMandatesList] = useState<Array<MandateListItem>>(mandateList ?? []);
    const [isMandateLoading, setIsMandateLoading] = useState<boolean>(false);

    const { selectedPortfolio } = useSelector((state: RootStateType) => state.selectedPortfiolio);

    const handleSelectMandateRadioBtn = (mandateId: string, mandateStatus: string, mandCreationDate: string, activeObj?: any) => {
        setSelectedMandateId(mandateId);
        setSelectedMandateStatus(mandateStatus);
        setMandateCreationDate(mandCreationDate);
        setActiveMandateObj(activeObj);
    }
    const theme = useTheme();

    useEffect(() => {
        (async function fetchMandateListByAmount() {
            try {
                setIsMandateLoading(true);
                const mandatesListRes = await (dispatch(getMandatesList({ amount: prefilledValues?.sipamount ?? 0, clientCode: clientCode, status: 'Active', portfolioId: selectedPortfolio?.value ?? "" })) as unknown as Promise<MandatesListResBody>);
                setFreshMandatesList(mandatesListRes?.mandates || []);
            } catch (e) {
                console.log('Error in fetching data');
            } finally {
                setIsMandateLoading(false);
            }
        })()
    }, [])

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth='lg'
            sx={(theme: Theme) => ({
                '& .MuiPaper-root': {
                    borderRadius: '25px',
                    backgroundColor: theme.palette.common.white,
                    padding: { xs: "10px", sm: "20px" },
                    width: "1063px",
                    maxHeight: "80%",
                     [theme.breakpoints.down('sm')]: {
                            margin: { xs: '3px' }
                        },
                },
            })}
        >
            <Box display={'flex'} flexDirection={'row'} gap={'10px'} mb={1}>
                {mandateDialogOptions?.map((item) => (
                    <Box
                        key={item.id}
                        component={'button'}
                        sx={{ display: 'flex', alignItems: 'center',justifyContent: 'center',width: { xs: '100%', sm: 'fit-content' }, gap: '10px', borderRadius: '10px', border: `1px solid ${theme.palette.text.secondary}80`, padding: '10px 15px', background: item.id === curMandateOption ? theme.palette.primary[500] + '1A' : 'transparent', cursor: 'pointer' }}
                        onClick={() => setCurMandateOption(item.id)}
                    >
                        {item.id === curMandateOption ? <CheckedRadio /> : <UncheckRadio />}
                        <Typography
                            variant="body_500"
                            color={item.id === curMandateOption ? theme.palette.primary[500] : theme.palette.text.secondary}
                        >
                            {item.label}
                        </Typography>
                    </Box>
                ))}
            </Box>
            {curMandateOption === 0 && (
                <>
                    <DialogContent
                        sx={{
                            padding:{ xs: "10px 12px", sm: "20px 24px" },
                            maxHeight: "100%",
                            overflowY: "auto",
                        }}
                    >
                        <MandateCreationForm
                            clientCode={clientCode}
                            onClose={onClose}
                            handleCreateMandateStatus={handleCreateMandateStatus}
                            prefilledValues={prefilledValues}
                        />
                    </DialogContent>
                    {/* <DialogActions
                        sx={{ justifyContent: "center" }}
                    >
                        <Button
                            variant="contained"
                            sx={(theme: Theme) => ({
                                ...theme.typography.body_600,
                                padding: "10px",
                                width: "50%"
                            })}
                            onClick={() => handleMandateSelection(selectedMandateId, selectedMandateStatus, mandateCreationDate)}
                        >
                            Save
                        </Button>
                    </DialogActions> */}
                </>
            )}
            {curMandateOption === 1 && (
                <>
                    <DialogContent
                        sx={{
                            maxHeight: "100%",
                            overflowY: "auto",
                            padding: { xs: "10px 10px 10px 10px", sm: "10px 20px 10px 10px" }
                        }}
                    >
                        <CustomTable
                            isLoading={isMandateLoading}
                            loadingRowsNum={2}
                            tableBody={
                                <MandateTableBody
                                    data={freshMandatesList}
                                    selectedMandateId={selectedMandateId}
                                    handleRadioBtnClick={handleSelectMandateRadioBtn}
                                />
                            }
                            tableData={freshMandatesList}
                            tableHeaderData={tableHeaderData}
                            tableDataCount={(freshMandatesList ?? []).length}
                            tableBodyLoaderRowCustomStyles={{
                                '& td:first-of-type': {
                                    paddingLeft: "0px"
                                },
                            }}
                            tableHeadRowCustomStyles={{
                                '& th:first-of-type': {
                                    paddingLeft: "0px"
                                }
                            }}
                            tableBodyCustomStyles={{
                                '& tr:last-child td': {
                                    border: 0
                                },
                            }}

                            manualResponsiveBody={true}
                            hideResponsiveHeader={true}
                            ManualResponsiveBodyComponent={
                                <MandateMobileBody
                                    data={freshMandatesList}
                                    selectedMandateId={selectedMandateId}
                                    handleRadioBtnClick={handleSelectMandateRadioBtn}
                                />
                            }

                            isPaginationVisible={false}
                        />
                    </DialogContent>
                    <DialogActions
                        sx={{ justifyContent: "center" }}
                    >
                        <Button
                            variant="contained"
                            sx={(theme: Theme) => ({
                                ...theme.typography.body_600,
                                padding: "10px",
                                width: "50%"
                            })}
                            onClick={() => handleMandateSelection(selectedMandateId, selectedMandateStatus, mandateCreationDate, activeMandateObj)}
                        >
                            Okay
                        </Button>
                    </DialogActions>
                </>
            )}
        </Dialog >
    )
}